




import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import serverContext from "../../../core/server-context";
import { DropRouteLeg } from "./routeModels";
import snackbar from "../../components/snackbar/snackbarClient";
import endpointService from "../../../core/api/endpoint.service";

const PolygonColor = "#5EB0B4";

@Component
export default class RouteMap extends Vue {
    @Prop({ default: () => [] }) private breadcrumb!: ReadonlyArray<google.maps.LatLngLiteral>;
    @Prop({ default: () => [] }) private waypoints!: google.maps.LatLngLiteral[];
    @Prop({ default: () => [] }) private directions!: ReadonlyArray<DropRouteLeg>;
    @Prop({ default: 0 }) private upcomingWaypoint!: number;
    @Prop({ default: false }) private showBreadcrumb!: boolean;
    @Prop() private selectedWaypoint!: number;

    private map!: google.maps.Map;
    private waypointMarkers: google.maps.Marker[] = [];
    private breadcrumbPolyLineWithCar; // dont have to be reactive
    private animationLoop = {
        run: true,
        scaleMin: 25,
        pulse: 25,
        scaleMax: 45,
        pulseSpeed: 0.1,
        countUp: false,
        icons: null
    };

    private async mounted() {
        const historyNoPoints = 100;
        this.createMap();
        const currentPos = await this.drawLastBreadcrumbAndCar(this.breadcrumb.slice(-historyNoPoints));
        this.drawWaypoints();
        this.setSelectedWaypoint(false);
        this.setBounds(currentPos);

        if (this.showBreadcrumb) {
            this.renderBreadcrumb();
        }

        try {
            // Draw rest of breadcrumb
            const noOfChunks = Math.ceil(this.breadcrumb.length / historyNoPoints) - 1;
            for (let i = 0; i < noOfChunks; i++) {
                await this.drawBreadcrumb(this.breadcrumb.slice(-(i + 2) * historyNoPoints, -(i + 1) * historyNoPoints));
            }
        } catch (e) {
            snackbar.error("Fejl ved tegning af historie-spor", e);
        }
    }

    private destroyed() {
        this.animationLoop.run = false;
    }

    @Watch("directions")
    private onDirectionsReady() {
        this.drawFutureRoute();
    }

    private createMap() {
        const element = this.$refs["map"];

        const mapStyles = require("../../util/mapStyleDetailed.json");

        const styledMapType = new google.maps.StyledMapType(mapStyles, { name: "styles map" });

        const options: google.maps.MapOptions = {
            disableDoubleClickZoom: true,
            fullscreenControl: false,
            keyboardShortcuts: false,
            mapTypeControl: false,
            rotateControl: false,
            scaleControl: false,
            streetViewControl: false
        };

        this.map = new google.maps.Map(element as HTMLBaseElement, options);

        this.map.mapTypes.set("styled_map", styledMapType);
        this.map.setMapTypeId("styled_map");
    }

    private setBounds(currentPos: google.maps.LatLngLiteral | null) {
        const bounds = new google.maps.LatLngBounds();

        if (currentPos !== null) {
            bounds.extend(new google.maps.LatLng(currentPos.lat, currentPos.lng));
        }

        const upcomingWaypointIndex = this.upcomingWaypoint - 1;
        if (upcomingWaypointIndex >= 0 && this.waypoints.length > 0 && upcomingWaypointIndex < this.waypoints.length) {
            const upcomingWaypoint = this.waypoints[upcomingWaypointIndex];
            bounds.extend(new google.maps.LatLng(upcomingWaypoint.lat, upcomingWaypoint.lng));
        }

        this.map.fitBounds(bounds);
    }

    private drawWaypoints() {
        if (this.waypoints.length === 0) {
            return;
        }

        const scale = 15;

        this.waypoints.forEach((waypoint, ix) => {
            let fillColor;
            let labelColor;
            if ((this.upcomingWaypoint && (ix + 1) < this.upcomingWaypoint) || ix === this.selectedWaypoint) {
                fillColor = PolygonColor;
                labelColor = "white";
            } else if (ix !== this.selectedWaypoint) {
                fillColor = "white";
                labelColor = "#808080";
            }

            const marker = new google.maps.Marker({
                position: waypoint,
                label: { text: ix + 1 + "", color: labelColor },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillOpacity: 1,
                    strokeColor: PolygonColor,
                    fillColor,
                    strokeWeight: 2,
                    scale
                },
                zIndex: (ix === this.selectedWaypoint) ? 200 : 100,
                map: this.map
            });

            marker.addListener("click", () => {
                this.$emit("updateDrop", ix);
            });

            // tslint:disable-next-line:no-unused-expression
            this.waypointMarkers.push(marker);

            // draw background circle for selected
            // tslint:disable-next-line:no-unused-expression
            this.waypointMarkers.push(
                new google.maps.Marker({
                    position: waypoint,
                    opacity: ix === this.selectedWaypoint ? 1 : 0,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillOpacity: 0.20,
                        strokeColor: PolygonColor,
                        fillColor: PolygonColor,
                        strokeWeight: 0.0,
                        scale: scale * 2
                    },
                    zIndex: 90,
                    map: this.map
                })
            );
        });
    }

    private renderBreadcrumb() {
        if (this.breadcrumb.length === 0) {
            return;
        }

        const scale = 15;

        this.breadcrumb.forEach((breadcrumb, ix) => {
            const fillColor = "red";
            const labelColor = "white";

            const marker = new google.maps.Marker({
                position: breadcrumb,
                label: { text: ix + 1 + "", color: labelColor },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillOpacity: 1,
                    strokeColor: labelColor,
                    fillColor,
                    strokeWeight: 2,
                    scale
                },
                zIndex: (ix === this.selectedWaypoint) ? 200 : 100,
                map: this.map
            });

            marker.addListener("click", () => {
                this.$emit("updateDrop", ix);
            });

            // tslint:disable-next-line:no-unused-expression
            this.waypointMarkers.push(marker);

            // draw background circle for selected
            // tslint:disable-next-line:no-unused-expression
            this.waypointMarkers.push(
                new google.maps.Marker({
                    position: breadcrumb,
                    opacity: ix === this.selectedWaypoint ? 1 : 0,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillOpacity: 0.20,
                        strokeColor: PolygonColor,
                        fillColor: PolygonColor,
                        strokeWeight: 0.0,
                        scale: scale * 2
                    },
                    zIndex: 90,
                    map: this.map
                })
            );
        });
    }

    @Watch("selectedWaypoint")
    private onSelectedWaypointChange(value) {
        if (value == null) {
            return;
        }
        this.setSelectedWaypoint(true);
    }

    private setSelectedWaypoint(shouldZoom: boolean) {
        if (this.waypointMarkers.length === 0) {
            // Not ready yet
            return;
        }
        this.waypointMarkers.forEach((marker: google.maps.Marker, ix) => {
            if (ix % 2) {
                // Clear all background-circles
                marker.setOpacity(0);
            }
        });

        this.waypointMarkers[(this.selectedWaypoint * 2 + 1)].setOpacity(1);

        if (shouldZoom) {
            setTimeout(() => {
                // Pan and zoom.
                const waypoint = this.waypointMarkers[this.selectedWaypoint * 2];
                this.map.panTo(waypoint.getPosition() || { lat: 0, lng: 0 });
                this.map.setZoom(13);
            }, 0);
        }
    }

    private async drawLastBreadcrumbAndCar(breadcrumb: google.maps.LatLngLiteral[]): Promise<google.maps.LatLngLiteral | null> {
        if (breadcrumb.length === 0) {
            return Promise.resolve(null);
        }

        const pulsate = {
            path: google.maps.SymbolPath.CIRCLE,
            fillOpacity: 0.20,
            strokeColor: PolygonColor,
            fillColor: PolygonColor,
            strokeWeight: 0.0,
            scale: 45
        };

        const background = {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: "white",
            fillOpacity: 1,
            strokeColor: PolygonColor,
            strokeOpacity: 0.30,
            scale: 25,
            strokeWeight: 1
        };

        //tslint:disable
        const car = {
            path: `M 27.600116416403058 27.878264880955488 C 27.470554702964684 27.878264880955488 27.357593768736066 27.728582508581155 27.357593768736066 27.60409751504658 C 27.357593768736066 27.479612521511946 27.470554702964684 27.348727405666654 27.600116416403058 27.348727405666654 L 27.668427633277748 27.348727405666654 C 27.79798934671602 27.348727405666654 27.911401711642313 27.479612521511946 27.911401711642313 27.60409751504658 C 27.911401711642313 27.728582508581155 27.79798934671602 27.878264880955488 27.668427633277748 27.878264880955488 L 27.600116416403058 27.878264880955488 Z M 23.910711886539783 26.89200706988603 L 23.910711886539783 25.06527311469398 C 23.910711886539783 24.841069374703434 24.092421567981983 24.65928688185831 24.316411065935746 24.65928688185831 L 27.41662020790349 24.65928688185831 C 27.640694873134432 24.65928688185831 27.82240455457672 24.841069374703434 27.82240455457672 25.06527311469398 L 27.82240455457672 26.89200706988603 L 23.910711886539783 26.89200706988603 Z M 25.032786351833824 27.878264880955488 C 24.991867446633936 27.878264880955488 24.958605212045505 27.797650081483084 24.958605212045505 27.75833457163033 C 24.958605212045505 27.719019282418085 24.991867446633936 27.68714200896819 25.032786351833824 27.68714200896819 L 26.700245142645933 27.68714200896819 C 26.74116382720523 27.68714200896819 26.774341115157014 27.719019282418085 26.774341115157014 27.75833457163033 C 26.774341115157014 27.797650081483084 26.74116382720523 27.878264880955488 26.700245142645933 27.878264880955488 L 25.032786351833824 27.878264880955488 Z M 24.06485870111199 27.878264880955488 C 23.935296987673716 27.878264880955488 23.820724715035944 27.728582508581155 23.820724715035944 27.60409751504658 C 23.820724715035944 27.479612521511946 23.935296987673716 27.348727405666654 24.06485870111199 27.348727405666654 L 24.13308497135004 27.348727405666654 C 24.262646684788425 27.348727405666654 24.37673906408922 27.479612521511946 24.37673906408922 27.60409751504658 C 24.37673906408922 27.728582508581155 24.262646684788425 27.878264880955488 24.13308497135004 27.878264880955488 L 24.06485870111199 27.878264880955488 Z M 28.766342171902323 24.754264944066726 C 28.65719743774564 24.754264944066726 28.56872452278056 24.83918929602563 28.56872452278056 24.94413943145621 L 28.56872452278056 25.691539876750163 C 28.56872452278056 25.754559028481232 28.60113618779924 25.810058086141364 28.65039177798507 25.84463269110605 C 28.630059965980823 25.960044942564053 28.526189425077888 26.048157330764752 28.40037065902749 26.048157330764752 L 28.26749117266588 26.048157330764752 L 28.26749117266588 24.744620080876224 C 28.26749117266588 24.740478656538205 28.26683895900883 24.034545849652794 28.26553475233526 22.626821660220006 C 28.26553475233526 22.505033777559607 28.188120786260363 22.406377636304 28.092587150984095 22.406377636304 L 23.640869517960198 22.406377636304 C 23.545420829320612 22.406377636304 23.4680070838863 22.505033777559607 23.4680070838863 22.626821660220006 L 23.4680070838863 24.744620080876224 L 23.4680070838863 26.048157330764752 L 23.30969170618042 26.048157330764752 C 23.183872940129955 26.048157330764752 23.080002399227112 25.960044942564053 23.059670587222758 25.84463269110605 C 23.108926177408588 25.81013994380942 23.141337842427358 25.754640665508667 23.141337842427358 25.691539876750163 L 23.141337842427358 24.94413943145621 C 23.141337842427358 24.83918929602563 23.052950094739504 24.754264944066726 22.94380513994217 24.754264944066726 L 22.74303983181101 24.754264944066726 C 22.633894877013702 24.754264944066726 22.54542196204859 24.83918929602563 22.54542196204859 24.94413943145621 L 22.54542196204859 25.691539876750163 C 22.54542196204859 25.79632629684465 22.633894877013702 25.8814143641396 22.74303983181101 25.8814143641396 L 22.86018124376639 25.8814143641396 C 22.87285682644057 26.080198098910152 23.042911829332958 26.23794996048608 23.252949779937666 26.23794996048608 L 23.4680070838863 26.23794996048608 L 23.4680070838863 27.345972928232737 L 23.4680070838863 27.995697979803563 L 23.4680070838863 28.54145408475745 L 23.4680070838863 29.277084677100753 C 23.4680070838863 29.381952954863237 23.556479998851394 29.466877306822106 23.665624953648734 29.466877306822106 L 24.43440326810144 29.466877306822106 C 24.543633169535337 29.466877306822106 24.63202113786383 29.381952954863237 24.63202113786383 29.277084677100753 L 24.63202113786383 28.92112120186823 L 27.10330700477436 28.92112120186823 L 27.10330700477436 29.277084677100753 C 27.10330700477436 29.381952954863237 27.191865087016737 29.466877306822106 27.30092487453681 29.466877306822106 L 28.069873523544032 29.466877306822106 C 28.178933311064082 29.466877306822106 28.26749117266588 29.381952954863237 28.26749117266588 29.277084677100753 L 28.26749117266588 28.54145408475745 L 28.26749117266588 27.995697979803563 L 28.26749117266588 26.23794996048608 L 28.457112585270263 26.23794996048608 C 28.66715053587487 26.23794996048608 28.837205538767314 26.080198098910152 28.84988112144143 25.8814143641396 L 28.96710770067414 25.8814143641396 C 29.076167488194194 25.8814143641396 29.164640403159265 25.79632629684465 29.164640403159265 25.691539876750163 L 29.164640403159265 24.94413943145621 C 29.164640403159265 24.83918929602563 29.076167488194194 24.754264944066726 28.96710770067414 24.754264944066726 L 28.766342171902323 24.754264944066726 Z `,
            /*path: `M29 20.1H13c-.2 0-.3-.1-.3-.3V4.9c0-.2.1-.3.3-.3h7.1c.1 0 .2.1.3.1.1.1.1.2.2.3 0
                .1.1.1.2.2l.3.3c.1.1.2.1.3.2.1 0 .1.1.2.1.1.1.2.1.4.2 0 0 .1 0 .1.1.1.1.3.1.5.1h.2c.2 0
                .4.1.6.1s.4 0 .6-.1h.2c.2 0 .3-.1.5-.1.1 0 .1 0 .2-.1s.2-.1.3-.2c.1 0 .1-.1.2-.1 0 0 .1 0
                .2-.1l.3-.3.2-.2c.1-.1.1-.2.2-.3.1-.1.2-.1.3-.1H29c.3 0 .6-.3.6-.6s-.3-.6-.6-.6h-1.9c-.2
                0-.3-.1-.3-.3 0-1.8-1.6-3.2-3.5-3.2s-3.5 1.4-3.5 3.2c0 .2-.1.3-.3.3h-9.4c-.2 0-.3-.1-.3-.3C9.8 1.4 8.2
                0 6.3 0S2.8 1.4 2.8 3.2c0 .2-.1.3-.3.3H.6c-.3 0-.6.3-.6.6v6.1c.1.1.1.2.1.3L3 15.7c.1.2.3.3.6.3h5.7c.3
                0 .6-.3.6-.6s-.3-.6-.6-.6H7.4c-.2 0-.3-.1-.3-.3v-4.4c0-.3-.3-.6-.6-.6H1.6c-.2
                0-.3-.1-.3-.3V4.8c0-.2.1-.3.3-.3h1.5c.1 0 .2.1.3.1.1.1.1.2.2.3 0 .1.1.1.2.2l.3.3c.1.1.2.1.3.2.1 0
                .1.1.2.1.1.1.2.1.4.2 0 0 .1 0 .1.1.1.1.3.1.5.1h.2c.2 0 .4.1.6.1s.4 0 .6-.1h.2c.2 0 .3-.1.5-.1.1 0 .1
                0 .2-.1s.2-.1.3-.2c.1 0 .1-.1.2-.1.1.1.2.1.2 0l.3-.3.2-.2c.1-.1.1-.2.2-.3.1-.1.2-.1.3-.1h1.5c.2 0
                .3.1.3.3v15.7c0 .3.3.6.6.6h17c.3 0 .6-.3.6-.6s-.3-.6-.6-.6zM23.3 1.2c1.2 0 2.2.9 2.2 2 0 .2 0
                .4-.1.7-.2.6-.7 1-1.3 1.2H24c-.2.1-.4.1-.6.1s-.4
                0-.6-.1h-.1c-.6-.2-1.1-.6-1.3-1.2-.1-.2-.1-.4-.1-.7-.2-1.1.8-2 2-2zM2 10.9c.1-.1.2-.1.3-.1h3.2c.2 0
                .3.1.3.3v3.5c0 .2-.1.3-.3.3H4.2c-.1 0-.2-.1-.3-.2L2 11.2c-.1-.1-.1-.2
                0-.3zm6.4-7c-.2.6-.7 1-1.3 1.2-.3.1-.5.1-.7.1s-.4
                0-.6-.1h-.1c-.6-.2-1.1-.6-1.3-1.2-.1-.2-.1-.4-.1-.7-.1-1.1.8-2 2-2s2.2.9 2.2 2c0 .2 0 .4-.1.7z`,
      */      fillColor: "#4C7485",
            fillOpacity: 1,
            scale: 4,
            strokeColor: "transparent",
            anchor: new google.maps.Point(26, 26)
        };

        const path = breadcrumb.map(l => `${l.lat},${l.lng}`).join("|");
        const params = {
            key: serverContext.googleMapsApiKey,
            path,
            interpolate: "true"
        };
        const roadData: any = await endpointService.getAbsolute("https://roads.googleapis.com/v1/snapToRoads", params);

        if (roadData.warningMessage) {
            snackbar.error("Google roads returnerede en advarsel", roadData.warningMessage);
        }

        if (!roadData.snappedPoints) {
            snackbar.error("Google roads returnede ingen data");
            return Promise.resolve(null);
        }

        const currentPos = roadData.snappedPoints[roadData.snappedPoints.length - 1].location;
        if (roadData.snappedPoints.length === 1) {
            // Duplicate to make polyline
            roadData.snappedPoints.push(roadData.snappedPoints[0]);
        }

        this.breadcrumbPolyLineWithCar = new google.maps.Polyline({
            path: roadData.snappedPoints.map(elem => ({
                lat: elem.location.latitude,
                lng: elem.location.longitude
            })),
            strokeColor: PolygonColor,
            strokeWeight: 2,
            zIndex: 400,
            icons: [
                {
                    icon: pulsate,
                    offset: '100%'
                },
                {
                    icon: background,
                    offset: '100%'
                },
                {
                    icon: car,
                    offset: '100%',
                    fixedRotation: true
                }
            ]
        });
        this.breadcrumbPolyLineWithCar.setMap(this.map);

        requestAnimationFrame(this.animationInterval);

        return Promise.resolve({
            lat: currentPos.latitude,
            lng: currentPos.longitude
        });
    }

    private async drawBreadcrumb(breadcrumb: google.maps.LatLngLiteral[]) {
        if (breadcrumb.length === 0) {
            return;
        }

        const path = breadcrumb.map(l => `${l.lat},${l.lng}`).join("|");
        const params = {
            key: serverContext.googleMapsApiKey,
            path,
            interpolate: "true"
        };
        const roadData: any = await endpointService.getAbsolute("https://roads.googleapis.com/v1/snapToRoads", params);

        if (roadData.warningMessage) {
            snackbar.error("Google roads returnerede en advarsel", roadData.warningMessage);
        }

        if (roadData.snappedPoints.length === 1) {
            // Duplicate to make polyline
            roadData.snappedPoints.push(roadData.snappedPoints[0]);
        }

        const breadcrumbPolyLine = new google.maps.Polyline({
            path: roadData.snappedPoints.map(elem => ({
                lat: elem.location.latitude,
                lng: elem.location.longitude
            })),
            strokeColor: PolygonColor,
            strokeWeight: 2,
            zIndex: 400
        });
        breadcrumbPolyLine.setMap(this.map);
    }

    private drawFutureRoute() {
        if (!this.directions.length) {
            return;
        }

        this.directions.forEach((leg, ix) => {
            drawOneLeg(this.map, leg.steps, ix === 0);
        });

        function drawOneLeg(map: google.maps.Map, steps: google.maps.LatLngLiteral[], first: boolean) {
            if (first) {
                // line until first waypoint is not dashed
                const line = new google.maps.Polyline({
                    path: steps,
                    strokeOpacity: 1,
                    strokeColor: PolygonColor,
                    strokeWeight: 2,
                    map
                });
            } else {
                const lineSymbol = {
                    path: "M 0,-1 0,1",
                    strokeOpacity: 1,
                    strokeColor: PolygonColor,
                    scale: 2
                };

                const line = new google.maps.Polyline({
                    path: steps,
                    strokeOpacity: 0,
                    strokeColor: PolygonColor,
                    icons: [
                        {
                            icon: lineSymbol,
                            offset: "0",
                            repeat: "10px"
                        }
                    ],
                    map
                });
            }
        }
    }

    private animationInterval() {
        const icons = this.breadcrumbPolyLineWithCar.get("icons");
        const pulse = this.animationLoop.pulse;
        const pulseSpeed = this.animationLoop.pulseSpeed;
        const countUp = this.animationLoop.countUp;
        const scaleMax = this.animationLoop.scaleMax;
        const scaleMin = this.animationLoop.scaleMin;

        // pulsate
        if (pulse >= scaleMax && countUp) {
            this.animationLoop.countUp = false;
        } else if (pulse <= scaleMin && !countUp) {
            this.animationLoop.countUp = true;
        }
        this.animationLoop.pulse = countUp ? pulse + pulseSpeed : pulse - pulseSpeed;
        icons[0].icon.scale = pulse;
        this.breadcrumbPolyLineWithCar.set("icons", icons);
        if (this.animationLoop.run) {
            requestAnimationFrame(this.animationInterval);
        }
    }

}
